import { createContext, useContext, useEffect, useRef } from 'react';
import { brandConfig } from '../brandConfig';

/**
 * Browser tab titles — `<Page> | <Site>` on every route (2026-09-11). The
 * module is never a separate part of the tab (Tom, 2026-09-11): only the page's
 * own name, then the site.
 *
 * Standard: vault/how-to/how-to-build-new-module.md § "Browser tab titles".
 *
 * Two layers, one writer:
 *   - PLATFORM: `components/DocumentTitleManager.tsx`, mounted once around
 *     `<Routes>` in App.tsx, matches the URL against every module's routes and
 *     derives the title with `deriveRouteTitle()` below. No module has to do
 *     anything for its pages to get a correct tab.
 *   - MODULE (optional): `usePageTitle("…")` replaces the <Page> part. Detail
 *     pages pass the record's name ("Jane Smith"); list pages only call it when
 *     the derived name reads badly.
 *
 * The module name is used for exactly two things, never as its own segment:
 *   - a module's root route with no nav label IS the module ("Calendars");
 *   - a bare generic page name is not a name ("Dashboard" ×12 tabs), so it is
 *     read with its module: "Admissions Dashboard", "SMS Settings".
 *
 * The manager is the ONLY thing that writes `document.title`. Pages hand it
 * their title through context instead of writing it themselves — a page's
 * effect runs BEFORE its parent layout's effect, so any "layout sets the
 * title" scheme silently overwrites every page. Never write `document.title`
 * from a module.
 *
 * The site part is always `brandConfig.siteName` — never a string literal, or
 * every tenant's tabs read "Tom Miller Services".
 */

export const TITLE_SEPARATOR = ' | ';

/**
 * Fired by `applyTenantBranding()` (brandConfig.ts) after the runtime overlay
 * rewrites `brandConfig.siteName`, so tabs re-brand without a reload. The
 * literal is repeated there (importing this file from brandConfig would be
 * circular) — keep the two in step.
 */
export const BRANDING_APPLIED_EVENT = 'tms:branding-applied';

/**
 * The slice of a module config a tab title reads. Structural on purpose — no
 * import of types/ModuleConfig — so every tenant's (often looser) module types
 * fit, and this file depends on nothing but brandConfig.
 */
export interface TitleSourceModule {
  name: string;
  navigation?: unknown;
  routes?: ReadonlyArray<{ path?: string } | undefined>;
}

export interface RouteTitleParts {
  /** The page part, e.g. "Award Classifications". Absent on a module's root route. */
  page?: string;
  /** The module's name as a tab ("Employees") — the page name on the module's root route. */
  module?: string;
  /** The module's name as its author wrote it ("Employee", from "Employee Management") — qualifies a generic page name. */
  qualifier?: string;
}

// ---------------------------------------------------------------------------
// Pure helpers (unit-tested in hooks/__tests__/useDocumentTitle.test.ts)
// ---------------------------------------------------------------------------

const ACRONYMS: Record<string, string> = {
  abn: 'ABN', acfi: 'ACFI', adp: 'ADP', ai: 'AI', api: 'API', ato: 'ATO', cdn: 'CDN', cms: 'CMS', crm: 'CRM',
  csv: 'CSV', dns: 'DNS', faq: 'FAQ', gst: 'GST', hq: 'HQ', id: 'ID', ipfs: 'IPFS', kpi: 'KPI',
  lm: 'LM', ndis: 'NDIS', pdf: 'PDF', pci: 'PCI', qr: 'QR', rsvp: 'RSVP', seo: 'SEO', sms: 'SMS',
  sop: 'SOP', ssl: 'SSL', tfn: 'TFN', url: 'URL', zk: 'ZK',
};
const WORD_FIXES: Record<string, string> = { whats: "What's" };
const UNCHANGED_PLURAL = /(ss|ics|us|is|ing)$/i;
const IRREGULAR_SINGULAR: Record<string, string> = { series: 'series', news: 'news', people: 'person' };

const tidy = (s?: string | null): string =>
  (s ?? '')
    // leading emoji / bullets / arrows on nav labels ("📊 Dashboard")
    .replace(/^[^A-Za-z0-9'"(]+/, '')
    .replace(/\s+/g, ' ')
    .trim();

/** Apply `fn` to the last plain word, skipping trailing tokens like "v2": "Proposals v2" → "Proposal v2". */
const mapLastWord = (phrase: string, fn: (w: string) => string): string => {
  const words = phrase.split(' ');
  for (let i = words.length - 1; i >= 0; i--) {
    if (/^[A-Za-z]+$/.test(words[i])) {
      words[i] = fn(words[i]);
      break;
    }
  }
  return words.join(' ');
};

/** "Employee" → "Employees", "Company" → "Companies", "Learning" → "Learning". */
export const pluralise = (phrase: string): string =>
  mapLastWord(phrase, (w) => {
    if (!/^[A-Za-z]+$/.test(w) || w === w.toUpperCase() || UNCHANGED_PLURAL.test(w) || /s$/i.test(w)) return w;
    if (/[^aeiou]y$/i.test(w)) return w.slice(0, -1) + 'ies';
    if (/(x|ch|sh)$/i.test(w)) return w + 'es';
    return w + 's';
  });

/** "Employees" → "Employee", "Companies" → "Company", "Analytics" → "Analytics". */
export const singularise = (phrase: string): string =>
  mapLastWord(phrase, (w) => {
    if (!/^[A-Za-z]+$/.test(w) || w === w.toUpperCase() || UNCHANGED_PLURAL.test(w)) return w;
    const irregular = IRREGULAR_SINGULAR[w.toLowerCase()];
    if (irregular) return w.charAt(0) + irregular.slice(1);
    if (/ies$/i.test(w)) return w.slice(0, -3) + 'y';
    if (/(xes|ches|shes|sses)$/i.test(w)) return w.slice(0, -2);
    if (/s$/i.test(w)) return w.slice(0, -1);
    return w;
  });

/** URL segment → words: "notification-rules" → "Notification Rules", "ai-usage" → "AI Usage". */
export const humanise = (segment: string): string =>
  segment
    .split(/[-_]+/)
    .filter(Boolean)
    .map((w) => {
      const lower = w.toLowerCase();
      return ACRONYMS[lower] ?? WORD_FIXES[lower] ?? lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');

/**
 * The module part of the tab: "Employee Management" → "Employees",
 * "Chloe (clinical assistant)" → "Chloe"; other names unchanged.
 */
export const moduleTabLabel = (name: string): string => {
  const n = tidy(name).replace(/\s*\([^)]*\)$/, '');
  const m = n.match(/^(.*\S)\s+Management$/i);
  return m ? pluralise(m[1]) : n;
};

interface NavLike {
  label?: unknown;
  path?: unknown;
  subItems?: unknown;
}

// A few legacy configs carry `navigation` as an object; only arrays are nav lists.
const navItems = (items: unknown): NavLike[] =>
  (Array.isArray(items) ? (items as NavLike[]) : []).flatMap((item) => (item ? [item, ...navItems(item.subItems)] : []));

/** A nav label as a tab name: grouped labels keep only the item ("Manage · Settings" → "Settings"). */
const navLabel = (label: unknown): string => tidy(String(label ?? '').split(' · ').pop());

const findNavLabel = (items: unknown, path: string): string | undefined =>
  navItems(items)
    .filter((item) => item.path === path)
    .map((item) => navLabel(item.label))
    .find(Boolean);

const compact = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
const LEADING_VERB = /^(all|my|new|add|manage|edit|create|view|issue)\s+/i;

const isParam = (seg: string) => seg.startsWith(':') || seg === '*';
const ACTION_WORDS = new Set(['new', 'create', 'edit', 'view', 'details']);
/** URL namespaces, not nouns: "/vlog/admin/:id/edit" edits a vlog, not an admin. */
const NAMESPACE_WORDS = new Set(['admin', 'manage']);

/**
 * The <Page> part a URL pattern implies, from the path alone:
 *   /employees/new             → "New Employee"
 *   /employees/:id             → "Employee"
 *   /employees/edit/:id        → "Edit Employee"   (also /employees/:id/edit)
 *   /calendars/:id/view        → "Calendar"
 *   /employees/roles           → "Roles"
 *   /calendars/:id/event-types → "Event Types"
 *   /employees                 → undefined (the module's root — the module part says it)
 */
export const pageFromPath = (path: string, moduleLabel: string, vocabulary: string[] = []): string | undefined => {
  const segs = path.split('/').filter(Boolean);
  if (segs.length <= 1) return undefined;

  // A slug in the module's own words: "companyknowledge" → "Company Knowledge",
  // "seometadata" → "SEO Metadata" (from the nav), "state-of-play" → "State of Play".
  const capitalised = (p: string) => p.charAt(0).toUpperCase() + p.slice(1);
  const phraseFor = (seg: string): string => {
    const human = humanise(seg).replace(/\s+Management$/i, '');
    const exact = vocabulary.find((v) => compact(v) === compact(seg));
    if (exact) return capitalised(exact); // nav labels can be sentence-case ("Create course" → "course")
    const sameNoun = vocabulary.find((v) => compact(singularise(v)) === compact(singularise(human)));
    if (!sameNoun) return human;
    // Keep the URL's number: "/incidents/:id/risk-assessment" is ONE assessment.
    return capitalised(compact(human) === compact(singularise(human)) ? singularise(sameNoun) : sameNoun);
  };

  // The noun a param/action refers to: the nearest static, non-action segment before index i.
  const nounBefore = (i: number): string => {
    for (let j = i - 1; j >= 0; j--) {
      const s = segs[j];
      const low = s.toLowerCase();
      if (isParam(s) || ACTION_WORDS.has(low) || NAMESPACE_WORDS.has(low)) continue;
      // Opaque slugs ("/p", "/lm") read worse than the module's own name.
      if (s.length < 3 || !/^[a-z][a-z0-9-_]*$/i.test(s)) break;
      return singularise(phraseFor(s));
    }
    return singularise(moduleLabel);
  };

  const lastIdx = segs.length - 1;
  const at = (word: string) => segs.findIndex((s) => s.toLowerCase() === word);
  const newIdx = Math.max(at('new'), at('create'));
  const editIdx = at('edit');
  const last = segs[lastIdx].toLowerCase();

  if (newIdx >= 0) return `New ${nounBefore(newIdx)}`;
  if (editIdx >= 0) return `Edit ${nounBefore(editIdx)}`;
  if (last === 'view' || last === 'details' || isParam(segs[lastIdx])) return nounBefore(lastIdx);
  return phraseFor(segs[lastIdx]);
};

/** Title parts for one module route: nav label if the route is in the nav, else the path. */
export const deriveRouteTitle = (moduleConfig: Pick<TitleSourceModule, 'name' | 'navigation'>, routePath: string): RouteTitleParts => {
  const module = moduleTabLabel(moduleConfig.name);
  const vocabulary = [
    module, // the label ("Tenants"), never the raw "Tenant Management"
    ...navItems(moduleConfig.navigation).map((item) => navLabel(item.label).replace(LEADING_VERB, '')),
  ].filter(Boolean);
  const page = findNavLabel(moduleConfig.navigation, routePath) ?? pageFromPath(routePath, module, vocabulary);
  const qualifier = tidy(moduleConfig.name).replace(/\s*\([^)]*\)$/, '').replace(/\s+Management$/i, '');
  return { page, module, qualifier };
};

const lower = (s: string) => s.toLowerCase();
const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** Does the page part already name the module? Whole words, optional plural "s". */
const mentions = (page: string, label: string): boolean =>
  [label, singularise(label)].some(
    (l) => !!l && new RegExp(`(^|[^a-z0-9])${escapeRegExp(lower(l))}s?([^a-z0-9]|$)`).test(lower(page))
  );

/**
 * Page names that say nothing on their own — twelve tabs reading "Dashboard".
 * These are read with their module's name ("Admissions Dashboard").
 */
const GENERIC_PAGE_NAMES = new Set([
  'admin', 'administration', 'alerts', 'analytics', 'config', 'configuration', 'dashboard', 'help',
  'history', 'home', 'manage', 'notification rules', 'notification settings', 'notifications',
  'overview', 'register', 'report', 'reports', 'settings', 'statistics', 'templates', 'upload',
  "what's new",
]);

export const isGenericPageName = (page: string): boolean => GENERIC_PAGE_NAMES.has(tidy(page).toLowerCase());

const SEGMENT_SEPARATOR = /(\s+[-–—|·]\s+)/;

/**
 * Drop a segment that only repeats the module's name — existing page titles
 * often carry one ("AI Trainer — Email", "Media CDN - Upload",
 * "What's New - Calendars"). Nothing is dropped if it would leave nothing.
 */
const withoutModuleSegment = (page: string, names: string[]): string => {
  if (!names.length) return page;
  const tokens = page.split(SEGMENT_SEPARATOR); // [seg, sep, seg, sep, seg…]
  if (tokens.length < 3) return page;
  const key = (x: string) => compact(singularise(tidy(x))); // "Business Snapshots" ≡ "Business Snapshot"
  const aliases = new Set(names.flatMap((n) => [n, n.split(' ')[0]]).map(key).filter(Boolean));
  const kept: string[] = [];
  for (let i = 0; i < tokens.length; i += 2) {
    if (aliases.has(key(tokens[i]))) continue;
    if (kept.length) kept.push(tokens[i - 1]);
    kept.push(tokens[i]);
  }
  if (!kept.length || kept.length === tokens.length) return page;
  const joined = kept.join('');
  return joined.charAt(0).toUpperCase() + joined.slice(1);
};

/** A generic page name read with its module: "Dashboard" → "Admissions Dashboard", "Settings" → "Profile Settings". */
const qualified = (page: string, qualifier: string, module: string): string => {
  if (!qualifier || !isGenericPageName(page) || mentions(page, qualifier) || (module && mentions(page, module))) return page;
  const generic = page.toLowerCase();
  if (generic === "what's new") return `What's New in ${module || qualifier}`;
  if (generic === 'manage') return `Manage ${qualifier}`;
  return `${qualifier} ${page}`;
};

/**
 * The tab: `<Page> | <Site>`. The module never gets its own segment — it only
 * names a root route and qualifies a generic page name (see GENERIC_PAGE_NAMES).
 */
export const formatDocumentTitle = ({
  page,
  module,
  qualifier,
  site,
}: {
  page?: string | null;
  module?: string | null;
  qualifier?: string | null;
  site?: string | null;
}): string => {
  const m = tidy(module);
  const q = tidy(qualifier) || m;
  const p = withoutModuleSegment(tidy(page), [m, q].filter(Boolean));
  const name = p ? qualified(p, q, m) : m;
  const s = (site ?? '').trim();
  if (!name || lower(name) === lower(s)) return s;
  return s ? `${name}${TITLE_SEPARATOR}${s}` : name;
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export interface DocumentTitleRegistry {
  /** Set (or replace) this caller's <Page> override. The most recent caller wins. */
  set: (key: object, pageTitle: string) => void;
  clear: (key: object) => void;
}

/** Provided by DocumentTitleManager. `null` = not mounted (un-ported tenant, isolated test render). */
export const DocumentTitleContext = createContext<DocumentTitleRegistry | null>(null);

/**
 * Name the current page in the browser tab. Pass the record's name on detail
 * pages. Falsy = no override (the derived title stands).
 *
 * Without a DocumentTitleManager above it, writes `<title> | <Site>` directly —
 * the pre-2026-09-11 behaviour — so a module synced to a tenant that has not
 * got the manager yet still works.
 */
export const usePageTitle = (title?: string | null) => {
  const registry = useContext(DocumentTitleContext);
  const key = useRef<object>({}).current;

  useEffect(() => {
    if (!title) return;
    if (registry) {
      registry.set(key, title);
      return () => registry.clear(key);
    }
    document.title = formatDocumentTitle({ page: title, site: brandConfig.siteName });
    return undefined;
  }, [title, registry, key]);
};

/**
 * @deprecated Kept for its one caller's signature (calendars CalendarView). The
 * manager derives the route's title from the module config on its own now;
 * this only forwards the custom title to `usePageTitle`.
 */
export const useDocumentTitle = (_moduleConfig?: unknown, customTitle?: string) => {
  usePageTitle(customTitle);
};
